import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useCallback, useState } from 'react';
// hooks
import { useLocalStorage } from 'src/hooks/use-local-storage';
// utils
import { localStorageGetItem } from 'src/utils/storage-available';
//
import { SettingsValueProps } from '../types';
import { SettingsContext } from './settings-context';

// ----------------------------------------------------------------------

// Get current language without importing the language manager (to avoid circular dependency)
const getCurrentLanguage = (): string => {
  const stored = localStorageGetItem('foodo-language', 'ar');
  return stored || 'ar';
};

type SettingsProviderProps = {
  children: React.ReactNode;
  defaultSettings: SettingsValueProps;
};

export function SettingsProvider({ children, defaultSettings }: SettingsProviderProps) {
  const [openDrawer, setOpenDrawer] = useState(false);

  // Get initial direction from current language
  const currentLanguage = getCurrentLanguage();
  const initialSettings = {
    ...defaultSettings,
    themeDirection: currentLanguage === 'ar' ? 'rtl' : 'ltr',
  };

  const [settings, setSettings] = useLocalStorage('settings', initialSettings);

  const onUpdate = useCallback(
    (name: string, value: string | boolean) => {
      setSettings((prevState: SettingsValueProps) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [setSettings]
  );

  // Listen for language changes from the centralized manager
  useEffect(() => {
    const handleLanguageChange = (event: any) => {
      const newLanguage = event.detail.language;
      const expectedDirection = newLanguage === 'ar' ? 'rtl' : 'ltr';

      if (settings.themeDirection !== expectedDirection) {
        onUpdate('themeDirection', expectedDirection);
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, [settings.themeDirection, onUpdate]);

  // Direction by lang
  const onChangeDirectionByLang = useCallback(
    (lang: string) => {
      const newDirection = lang === 'ar' ? 'rtl' : 'ltr';
      console.log('Changing direction to:', newDirection, 'for language:', lang);
      onUpdate('themeDirection', newDirection);
    },
    [onUpdate]
  );

  // Reset
  const onReset = useCallback(() => {
    setSettings(defaultSettings);
  }, [defaultSettings, setSettings]);

  // Drawer
  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const canReset = !isEqual(settings, defaultSettings);

  const memoizedValue = useMemo(
    () => ({
      ...settings,
      onUpdate,
      // Direction
      onChangeDirectionByLang,
      // Reset
      canReset,
      onReset,
      // Drawer
      open: openDrawer,
      onToggle: onToggleDrawer,
      onClose: onCloseDrawer,
    }),
    [
      onReset,
      onUpdate,
      settings,
      canReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      onChangeDirectionByLang,
    ]
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
